import { formatLocalDate } from '../../utils/localDateTime';
import React, { useState, useEffect } from 'react';
import { db } from '../../../firebaseConfig';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  Area, 
  AreaChart 
} from 'recharts';

interface TrendData {
  timestamp: any;
  riskScore: number;
  date: string;
}

interface RiskTrendChartProps {
  iso3: string;
  height?: number;
}

const RiskTrendChart: React.FC<RiskTrendChartProps> = ({ iso3, height = 200 }) => {
  const [data, setData] = useState<TrendData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTrends = async () => {
      setLoading(true);
      try {
        const trendsRef = collection(db, "daily_country_analysis", iso3, "historical_trends");
        const q = query(trendsRef, orderBy("timestamp", "asc"), limit(30));
        const snapshot = await getDocs(q);
        
        const mappedData = snapshot.docs.map(doc => {
          const d = doc.data();
          const date = d.timestamp?.toDate ? d.timestamp.toDate() : new Date();
          return {
            timestamp: d.timestamp,
            riskScore: d.riskScore,
            date: formatLocalDate(date)
          };
        });

        setData(mappedData);
      } catch (err: any) {
        // Silently handle permission/missing-collection errors — historical_trends may not
        // exist yet for all countries and this is a non-critical UI enhancement.
        // Only log unexpected errors (not standard Firestore "not found" or "permissions" denials).
        if (err?.code !== 'permission-denied' && err?.code !== 'not-found') {
          console.warn("RiskTrendChart: unexpected error fetching trend data:", err?.code || err);
        }
        // Leave data as [] so the "Insufficient telemetry" fallback renders cleanly.
      } finally {
        setLoading(false);
      }
    };

    if (iso3) fetchTrends();
  }, [iso3]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6 bg-slate-900/30 rounded border border-slate-800" style={{ height }}>
        <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (data.length < 2) {
    return (
      <div className="flex items-center justify-center p-6 bg-slate-900/30 rounded border border-slate-800 text-slate-500 text-[10px] uppercase font-bold tracking-widest" style={{ height }}>
        Insufficient historical telemetry for trend analysis
      </div>
    );
  }

  return (
    <div className="w-full bg-slate-900/30 p-2 rounded border border-slate-800" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data}>
          <defs>
            <linearGradient id="colorRisk" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
          <XAxis 
            dataKey="date" 
            axisLine={false} 
            tickLine={false} 
            tick={{ fill: '#64748b', fontSize: 9 }}
            interval="preserveStartEnd"
          />
          <YAxis 
            domain={[0, 100]} 
            axisLine={false} 
            tickLine={false} 
            tick={{ fill: '#64748b', fontSize: 9 }}
            width={20}
          />
          <Tooltip 
            contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', fontSize: '10px', color: '#f1f5f9' }}
            itemStyle={{ color: '#3b82f6', fontWeight: 'bold' }}
          />
          <Area 
            type="monotone" 
            dataKey="riskScore" 
            stroke="#3b82f6" 
            strokeWidth={2}
            fillOpacity={1} 
            fill="url(#colorRisk)" 
            animationDuration={1500}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default RiskTrendChart;
